import React, { useState, useEffect } from 'react';
import { Navigation } from '@/components/layout/Navigation';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye, 
  FileText,
  User,
  Mail,
  Calendar,
  AlertTriangle,
  Loader2,
  Download,
  Heart
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardPage = () => {
  const { user, token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [proofLoading, setProofLoading] = useState(false);
  const [proofData, setProofData] = useState(null);

  // Check if user has admin access
  const hasAdminAccess = user?.subscription_plan === 'festival' || 
                         user?.subscription_plan === 'enterprise' || 
                         user?.role === 'admin';

  useEffect(() => {
    if (hasAdminAccess) {
      fetchApplications(activeTab);
    }
  }, [activeTab, hasAdminAccess]);

  const fetchApplications = async (status) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/hardship-applications?status_filter=${status}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const viewApplication = async (application) => {
    setSelectedApplication(application);
    setProofData(null);
    setViewDialogOpen(true);
    
    // Fetch full application with proof
    setProofLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/hardship-applications/${application.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProofData(response.data);
    } catch (error) {
      console.error('Error fetching application details:', error);
      toast.error('Failed to load proof document');
    } finally {
      setProofLoading(false);
    }
  };

  const approveApplication = async (applicationId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/admin/hardship-applications/${applicationId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application approved! User now has hardship access.');
      setViewDialogOpen(false);
      fetchApplications(activeTab);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setProcessing(false);
    }
  };

  const rejectApplication = async () => {
    if (!selectedApplication) return;
    
    setProcessing(true);
    try {
      await axios.post(
        `${API}/api/admin/hardship-applications/${selectedApplication.id}/reject?rejection_reason=${encodeURIComponent(rejectionReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Application rejected.');
      setRejectDialogOpen(false);
      setViewDialogOpen(false);
      setRejectionReason('');
      fetchApplications(activeTab);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="max-w-4xl mx-auto px-4 py-12">
          <Card className="border-destructive">
            <CardContent className="p-12 text-center">
              <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
              <p className="text-muted-foreground">
                You need a Festival or Enterprise subscription to access the admin dashboard.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#5DBDB3]/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#5DBDB3]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-muted-foreground">Review hardship access applications</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => fetchApplications(activeTab)}
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="cursor-pointer hover:border-amber-300 transition-colors" onClick={() => setActiveTab('pending')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Review</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {applications.filter(a => a.status === 'pending').length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:border-green-300 transition-colors" onClick={() => setActiveTab('approved')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Approved</p>
                  <p className="text-2xl font-bold text-green-600">-</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:border-red-300 transition-colors" onClick={() => setActiveTab('rejected')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Rejected</p>
                  <p className="text-2xl font-bold text-red-600">-</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Applications List */}
        <Card>
          <CardHeader>
            <CardTitle>Hardship Applications</CardTitle>
            <CardDescription>
              Review proof of benefits and approve or reject applications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="pending" className="gap-2">
                  <Clock className="w-4 h-4" />
                  Pending
                </TabsTrigger>
                <TabsTrigger value="approved" className="gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Approved
                </TabsTrigger>
                <TabsTrigger value="rejected" className="gap-2">
                  <XCircle className="w-4 h-4" />
                  Rejected
                </TabsTrigger>
                <TabsTrigger value="all" className="gap-2">
                  All
                </TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab}>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : applications.length === 0 ? (
                  <div className="text-center py-12">
                    <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No {activeTab} applications</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {applications.map((app) => (
                      <div 
                        key={app.id} 
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-[#F08B7D]/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-[#F08B7D]" />
                          </div>
                          <div>
                            <p className="font-medium">{app.user_name || 'Unknown User'}</p>
                            <p className="text-sm text-muted-foreground">{app.user_email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">{app.reason}</Badge>
                              {getStatusBadge(app.status)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right text-sm text-muted-foreground">
                            <p>Submitted</p>
                            <p>{formatDate(app.created_at)}</p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => viewApplication(app)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Review
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>

      {/* View Application Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Review Application
            </DialogTitle>
            <DialogDescription>
              Review the proof of benefits and approve or reject this application
            </DialogDescription>
          </DialogHeader>

          {selectedApplication && (
            <div className="space-y-6 py-4">
              {/* Applicant Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <User className="w-3 h-3" /> Name
                  </p>
                  <p className="font-medium">{selectedApplication.user_name || 'Not provided'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email
                  </p>
                  <p className="font-medium">{selectedApplication.user_email}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Heart className="w-3 h-3" /> Situation
                  </p>
                  <Badge variant="secondary">{selectedApplication.reason}</Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Submitted
                  </p>
                  <p className="font-medium">{formatDate(selectedApplication.created_at)}</p>
                </div>
              </div>

              {/* Additional Info */}
              {selectedApplication.additional_info && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Additional Information</p>
                  <p className="text-sm bg-muted p-3 rounded-lg">{selectedApplication.additional_info}</p>
                </div>
              )}

              {/* Proof Document */}
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground font-medium">Proof of Benefits</p>
                {proofLoading ? (
                  <div className="flex items-center justify-center py-8 bg-muted rounded-lg">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : proofData?.proof_file_base64 ? (
                  <div className="border rounded-lg overflow-hidden">
                    {proofData.proof_content_type?.startsWith('image/') ? (
                      <img 
                        src={`data:${proofData.proof_content_type};base64,${proofData.proof_file_base64}`}
                        alt="Proof of benefits"
                        className="w-full max-h-[400px] object-contain bg-muted"
                      />
                    ) : (
                      <div className="p-8 text-center bg-muted">
                        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                        <p className="font-medium">{proofData.proof_filename}</p>
                        <p className="text-sm text-muted-foreground mb-4">{proofData.proof_content_type}</p>
                        <Button
                          variant="outline"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `data:${proofData.proof_content_type};base64,${proofData.proof_file_base64}`;
                            link.download = proofData.proof_filename;
                            link.click();
                          }}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Document
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-8 text-center bg-muted rounded-lg">
                    <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-muted-foreground">No proof document found</p>
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">Current Status</span>
                {getStatusBadge(selectedApplication.status)}
              </div>
            </div>
          )}

          {selectedApplication?.status === 'pending' && (
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => {
                  setRejectDialogOpen(true);
                }}
                disabled={processing}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button
                onClick={() => approveApplication(selectedApplication.id)}
                disabled={processing}
                className="bg-green-600 hover:bg-green-700"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Approve
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Application</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject this application? You can optionally provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Reason for rejection (optional)..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={rejectApplication}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Reject Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardPage;
